import { Button, FileEditAltIcon, FolderIcon, TextInput, ToggleInput } from "@datagame/ui";
import { sortBy } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";

import { Container, Heading, LeftSide, RightSide, Section } from "../Common";
import { ErrorMessage } from "../../../../../shared/ui/ErrorMessage";
import { FieldWrapper } from "../../../../../shared/ui/FieldWrapper";
import { ImageCover } from "../../../../../shared/ui/ImageCover";
import { useProject } from "../../../../../../contexts/project/ProjectContext";
import { ActionButtons } from "../Common/ActionButtons";
import { DIRECTORY_LIST } from "../../../../../../graphql/queries/ProjectQueries";
import { Folder } from "../../../../ProjectsView/FoldersSection/FoldersList";
import { ManageFoldersModal } from "../../../../../shared/modals/ManageFoldersModal";
import { groupFolders } from "../../../../ProjectsView/FoldersSection/FoldersList/FolderGroup/utils/groupFolders";
import { WithQueryLoader } from "../../../../../shared/WithQueryLoader";
import { useUser } from "../../../../../../hooks/user";
import { TooltipWithIcon } from "../../../../../shared/ui/TooltipWithIcon";
import { LeaderboardConfigurationUpdateInput, PlanName } from "../../../../../../gql/graphql";
import { Accordion } from "../../../../../shared/ui/Accordion";
import { LeaderboardConfigSection } from "../GameSettings/LeaderboardConfigSection";
import { LEADERBOARD_CONFIGURATION_UPDATE } from "../../../../../../graphql/mutations/GameMutation";

import styles from "./ConfigureProject.module.scss";

const HIDE_DATAGAME_BRANDING_PLANS: PlanName[] = [PlanName.Teams, PlanName.Professional];

export const ConfigureProject = () => {
  const {
    errors,
    project,
    settings: {
      title,
      publish,
      directoryId,
      setTitle,
      setPublish,
      setDirectoryId,
      hideDatagameBranding,
      setHideDatagameBranding,
      hideSummaryPage,
      setHideSummaryPage,
      showLeaderboard,
      setShowLeaderboard,
      leaderboardConfiguration,
      setLeaderboardConfiguration,
    },
  } = useProject();
  const { t: translation } = useTranslation();
  const location = useLocation();
  const t = (key: string) => translation(`project:configureProject.${key}`);

  const { data, loading } = useQuery(DIRECTORY_LIST);
  const { user } = useUser();
  const [isManageFoldersModalOpen, setIsManageFoldersModalOpen] = useState(false);
  const [updateLeaderboardConfig] = useMutation(LEADERBOARD_CONFIGURATION_UPDATE);

  const canHideDatagameBranding = useMemo(() => {
    return (
      user?.organization.manuallyManagedPlan === "ADMIN" ||
      HIDE_DATAGAME_BRANDING_PLANS.includes(user?.organization.subscription?.planName as PlanName)
    );
  }, []);

  const [selectedFolderId, setSelectedFolderId] = useState<string | undefined>(
    directoryId ? directoryId : location.state?.folder?.id,
  );

  const handleShowLeaderboardChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowLeaderboard(e.target.checked);
    // submit();
  };
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value);
  const handlePublishChange = (e: React.ChangeEvent<HTMLInputElement>) => setPublish(e.target.checked);
  const handleHideBrandingChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setHideDatagameBranding(e.target.checked);
  const handleFolderChange = (folder: Folder) => {
    setSelectedFolderId(folder.id);
  };
  const handleHideSummaryPageChange = (e: React.ChangeEvent<HTMLInputElement>) => setHideSummaryPage(e.target.checked);

  const folders = useMemo(() => {
    if (data?.directoryList) {
      return data.directoryList as Folder[];
    }
    return [];
  }, [data?.directoryList]);

  const childrenFolders = useMemo(() => {
    return folders?.filter((folder) => folder.parentDirectory?.id);
  }, [folders]);

  const sortedFolders = useMemo(() => {
    return sortBy(folders, "createdAt");
  }, [folders]);

  const groupedFolders = useMemo(() => {
    const parentFolders = sortedFolders?.filter((folder) => !folder.parentDirectory?.id);

    return groupFolders(parentFolders, childrenFolders);
  }, [sortedFolders]);

  useEffect(() => {
    if (!selectedFolderId && !directoryId) {
      const defaultFolder = folders.find((item) => item.default);
      setSelectedFolderId(defaultFolder?.id);
      return;
    }

    directoryId && setSelectedFolderId(directoryId);
  }, [directoryId, folders]);

  useEffect(() => {
    selectedFolderId && setDirectoryId(selectedFolderId);
  }, [selectedFolderId]);

  const selectedFolder = useMemo(() => {
    return folders.find((folder) => folder.id === selectedFolderId);
  }, [selectedFolderId, folders]);

  const handleLeaderboardConfigurationChange = (leaderboardData: LeaderboardConfigurationUpdateInput) => {
    if (!project) {
      setLeaderboardConfiguration(leaderboardData);
      return;
    }

    updateLeaderboardConfig({
      variables: {
        leaderboardConfigurationId: leaderboardConfiguration?.id,
        data: leaderboardData,
      },
    });
  };

  return (
    <WithQueryLoader isLoading={loading}>
      <Container>
        <RightSide>
          <ImageCover />
        </RightSide>
        <LeftSide>
          <Heading title={{ main: t("header1"), marked: t("header2") }}>{t("description")}</Heading>

          <div className={styles.fieldsWrapper}>
            <FieldWrapper
              tipClassName={styles.tip}
              className={styles.titleInputWrapper}
              label={t("form.title.label")}
              name="title"
              tip={t("form.title.tip")}
              withoutFormik={true}
            >
              <TextInput
                error={errors.title}
                className={styles.input}
                size="small"
                name="title"
                value={title}
                icon={<FileEditAltIcon />}
                onChange={handleTitleChange}
                placeholder={t("form.title.placeholder")}
              />
              {errors.title && <ErrorMessage className={styles.errorMessage}>{errors.title}</ErrorMessage>}
            </FieldWrapper>

            <FieldWrapper label={t("form.folder")} className={styles.folderWrapper} name="folder" withoutFormik={true}>
              <div className={styles.selectFolderWrapper}>
                <TextInput icon={<FolderIcon />} readOnly={true} size="small" value={selectedFolder?.name} />
                <Button className={styles.folderName} onClick={() => setIsManageFoldersModalOpen(true)}>
                  {t("form.selectFolder")}
                </Button>
              </div>
            </FieldWrapper>

            <Section title={t("form.preferences")}>
              <div className={styles.formField}>
                <ToggleInput
                  label={t("form.publish.label")}
                  name="publish"
                  onChange={handlePublishChange}
                  value={publish}
                  tooltip={<TooltipWithIcon content={t("form.publish.tooltip")} />}
                />
                {errors.publish && <ErrorMessage className={styles.errorMessage}>{errors.publish}</ErrorMessage>}
              </div>

              {canHideDatagameBranding && (
                <div className={styles.formField}>
                  <ToggleInput
                    label={t("form.hideBranding")}
                    name="hideDatagameBranding"
                    onChange={handleHideBrandingChange}
                    value={hideDatagameBranding}
                    tooltip={<TooltipWithIcon content={t("form.hideBrandingTooltip")} />}
                  />
                </div>
              )}

              <div className={styles.formField}>
                <ToggleInput
                  label={t("form.hideSummaryPage")}
                  name="hideSummaryPage"
                  onChange={handleHideSummaryPageChange}
                  value={hideSummaryPage}
                  tooltip={<TooltipWithIcon content={t("form.hideSummaryPageTooltip")} />}
                />
              </div>

              <div className={styles.formField}>
                <ToggleInput
                  label={t("form.showLeaderboard")}
                  name="showLeaderboard"
                  onChange={handleShowLeaderboardChange}
                  value={showLeaderboard}
                  tooltip={<TooltipWithIcon content={t("form.showLeaderboardTooltip")} />}
                />
              </div>

              {showLeaderboard && (
                <Accordion title={translation("project:gameSettings.form.leaderboardConfig")}>
                  <LeaderboardConfigSection
                    leaderboardConfiguration={leaderboardConfiguration}
                    onUpdate={handleLeaderboardConfigurationChange}
                  />
                </Accordion>
              )}
            </Section>

            <ActionButtons />
          </div>

          {isManageFoldersModalOpen && (
            <ManageFoldersModal
              onClose={() => setIsManageFoldersModalOpen(false)}
              folders={groupedFolders}
              selectedFolder={selectedFolder}
              setSelectedFolder={handleFolderChange}
              description={translation("folders:longDescription") ?? ""}
            />
          )}
        </LeftSide>
      </Container>
    </WithQueryLoader>
  );
};
